import { FormControl, FormControlLabel, FormHelperText, Switch } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { hasClickableFieldHelp, hasFieldHelpIcon, VNFieldHelpChrome, VNFieldHelpIconButton } from '@/components/VNFieldHelp';
import { useVNFieldHelpController } from '@/components/VNFieldHelp/useVNFieldHelpController';

type Props = Omit<VNFieldHelpProps, 'helperText'> & {
  name: string;
  label: string;
  /** Always-visible static text below the switch (not the click-expandable help panel). */
  helperText?: string;
  disabled?: boolean;
  forcedValue?: boolean;
};

const VNSwitchBody = ({ name, label, helperText, disabled, forcedValue }: Props) => {
  const { setValue, watch, control } = useFormContext();
  const isForceValue = forcedValue !== undefined;
  const myValue = watch(name);

  useEffect(() => {
    if (myValue === null) setValue(name, false);
  }, []);

  return (
    <FormControl>
      <Controller
        name={name}
        control={control}
        defaultValue={false}
        render={({ field }) => (
          <div>
            <FormControlLabel
              label={label}
              labelPlacement="start"
              control={
                <Switch
                  className="fuel-switch"
                  size="small"
                  {...field}
                  checked={Boolean(isForceValue ? forcedValue : myValue)}
                  disabled={disabled}
                />
              }
            />
            {helperText && <FormHelperText id="country-helper">{helperText}</FormHelperText>}
          </div>
        )}
      />
    </FormControl>
  );
};

const VNSwitchWithHelp = ({ name, label, helperText, disabled, forcedValue, ...fieldHelpProps }: Props) => {
  const { setValue, watch, control } = useFormContext();
  const isForceValue = forcedValue !== undefined;
  const myValue = watch(name);
  const {
    containerRef,
    panelOpen,
    drawerOpen,
    isActive,
    clickable,
    handleIconClick,
    drawerAriaLabel,
    closeDrawer,
    helperTooltip,
    helperText: fieldHelpText,
    helperContent,
    helperLink,
    helperContentTitle,
  } = useVNFieldHelpController(fieldHelpProps);

  useEffect(() => {
    if (myValue === null) setValue(name, false);
  }, []);

  const labelNode = (
    <span className="vn-field-help__label vn-field-help__label--inline">
      <span>{label}</span>
      <VNFieldHelpIconButton
        active={isActive}
        helperTooltip={helperTooltip}
        onClick={hasClickableFieldHelp(fieldHelpProps) ? handleIconClick : undefined}
        ariaLabel={drawerAriaLabel}
      />
    </span>
  );

  const rootClass = ['vn-switch', 'vn-field-help', panelOpen && 'vn-field-help--help-open'].filter(Boolean).join(' ');

  return (
    <div className={rootClass} ref={containerRef}>
      <FormControl>
        <Controller
          name={name}
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <div>
              <FormControlLabel
                label={labelNode}
                labelPlacement="start"
                control={
                  <Switch
                    className="fuel-switch"
                    size="small"
                    {...field}
                    checked={Boolean(isForceValue ? forcedValue : myValue)}
                    disabled={disabled}
                  />
                }
              />
              {helperText && <FormHelperText id="country-helper">{helperText}</FormHelperText>}
            </div>
          )}
        />
      </FormControl>
      <VNFieldHelpChrome
        panelOpen={panelOpen}
        drawerOpen={drawerOpen}
        helperText={fieldHelpText}
        helperLink={helperLink}
        helperContent={helperContent}
        helperContentTitle={helperContentTitle}
        onCloseDrawer={closeDrawer}
      />
    </div>
  );
};

const VNSwitch = ({ helperText, ...fieldHelpProps }: Props) => {
  if (hasFieldHelpIcon(fieldHelpProps)) {
    return <VNSwitchWithHelp helperText={helperText} {...fieldHelpProps} />;
  }

  return (
    <div className="vn-switch">
      <VNSwitchBody helperText={helperText} {...fieldHelpProps} />
    </div>
  );
};

export default VNSwitch;
